import { HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter, firstValueFrom, map, skip } from 'rxjs';
import { AnalysisPostsService } from './api/generated/analysis-posts/analysis-posts.service';
import { AutomaticMeasuresService } from './api/generated/automatic-measures/automatic-measures.service';
import { ManualMeasuresService } from './api/generated/manual-measures/manual-measures.service';
import { AnalysisPostDeletionImpact, ReferencingAnalysisPost } from './api/generated/model';
import { AnalysisPostStore } from '../stores/analysis-post.store';
import { AssetStore } from '../stores/asset.store';
import { AuscultationInformationStore } from '../stores/auscultation-info.store';
import { LocationStore } from '../stores/location.store';
import { ManualMeasureStore } from '../stores/manual-measure.store';
import { MeasureCheckerStore } from '../stores/measure-checker.store';
import { MeasureFormulaStore } from '../stores/measure-formula.store';
import { MeasureLocationStore } from '../stores/measure-location.store';
import { MeasureTypeStore } from '../stores/measure-type.store';
import { AutomaticMeasureStore } from '../stores/automatic-measure.store';
import { UnitStore } from '../stores/unit.store';
import { Asset } from '../types/asset.interface';
import { Location } from '../types/location.interface';
import { CheckerType, MeasureChecker } from '../types/measure-checker.interface';
import { MeasureFormula } from '../types/measure-formula.interface';
import { MeasureLocation } from '../types/measure-location.interface';
import { MeasureType } from '../types/measure-type.interface';
import { Measure, ReferenceableMeasure } from '../types/measure.interface';
import { Unit } from '../types/unit.interface';
import { isRequestCancellation } from '../utils/api-helpers';
import { RouteUtils } from '../utils/route.utils';

export type MeasureKind = 'manual' | 'automatic' | 'analysis-post';

interface TimeStampedEntity {
  startTs?: string | null;
}

type MeasureKindStore = {
  byId(): (id: number) => Measure | null;
  loadById(id: number): Promise<Measure>;
  getByAssetId(assetId: number): Promise<Measure[]>;
  entities(): Measure[];
  update(id: number, changes: Partial<Measure>): Promise<unknown>;
  delete(id: number): Promise<unknown>;
};

@Injectable()
export class MeasureDetailService {
  public readonly assetStore = inject(AssetStore);
  public readonly locationStore = inject(LocationStore);
  public readonly manualMeasureStore = inject(ManualMeasureStore);
  public readonly automaticMeasureStore = inject(AutomaticMeasureStore);
  public readonly analysisPostStore = inject(AnalysisPostStore);
  public readonly auscultationInfoStore = inject(AuscultationInformationStore);
  public readonly measureCheckerStore = inject(MeasureCheckerStore);
  public readonly measureFormulaStore = inject(MeasureFormulaStore);
  public readonly measureLocationStore = inject(MeasureLocationStore);
  public readonly measureTypeStore = inject(MeasureTypeStore);
  public readonly unitStore = inject(UnitStore);
  private readonly analysisPostApi = inject(AnalysisPostsService);
  private readonly manualMeasureApi = inject(ManualMeasuresService);
  private readonly automaticMeasureApi = inject(AutomaticMeasuresService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translateService = inject(TranslocoService);

  private readonly destroyRef = inject(DestroyRef);

  private readonly _kind = signal<MeasureKind | null>(null);
  private readonly _id = signal<number | null>(null);
  private readonly _dataLoaded = signal(false);
  private readonly _locationsMap = signal<Map<number, Location>>(new Map());
  private readonly _cycleSafeByMeasureId = signal<Map<number, boolean>>(new Map());
  private readonly _referencingAnalysisPosts = signal<ReferencingAnalysisPost[]>([]);
  private routeListenerActive = false;

  readonly kind = this._kind.asReadonly();
  readonly dataLoaded = this._dataLoaded.asReadonly();
  readonly referencingAnalysisPosts = this._referencingAnalysisPosts.asReadonly();

  private readonly activeStore: Signal<MeasureKindStore> = computed(() => {
    const kind = this._kind();
    const store =
      kind === 'automatic' ? this.automaticMeasureStore : kind === 'analysis-post' ? this.analysisPostStore : this.manualMeasureStore;
    return store as unknown as MeasureKindStore;
  });

  readonly measure = computed<Measure | null>(() => {
    const id = this._id();
    return id ? this.activeStore().byId()(id) : null;
  });

  readonly measureType = computed<MeasureType | null>(() => {
    const measure = this.measure();
    return measure ? (this.measureTypeStore.byId()(measure.measureTypeId) ?? null) : null;
  });

  readonly unit = computed<Unit | null>(() => {
    const measure = this.measure();
    return measure ? (this.unitStore.byId()(measure.unitId) ?? null) : null;
  });

  readonly asset = computed<Asset | null>(() => {
    const measure = this.measure();
    return measure ? (this.assetStore.byId()(measure.assetId) ?? null) : null;
  });

  readonly assetName = computed<string>(() => this.asset()?.name ?? '');

  readonly measureFormulas = computed<MeasureFormula[]>(() => {
    const id = this._id();
    return id ? this.measureFormulaStore.getFormulasByMeasureId()(id) : [];
  });

  readonly measureSortedFormulas = computed<MeasureFormula[]>(() =>
    this.measureFormulas().sort((a, b) => {
      const aMs = a.startTs ? new Date(a.startTs).getTime() : 0;
      const bMs = b.startTs ? new Date(b.startTs).getTime() : 0;
      if (aMs !== bMs) return bMs - aMs;
      return b.id - a.id;
    }),
  );

  readonly measureCheckers = computed<MeasureChecker[]>(() => {
    const id = this._id();
    return id ? this.measureCheckerStore.getCheckersByMeasureId()(id) : [];
  });

  readonly measureSortedCheckers = computed<MeasureChecker[]>(() => this.sortByDateDesc(this.measureCheckers()));

  readonly currentChecker = computed<MeasureChecker | null | undefined>(() => {
    const id = this._id();
    return id ? this.measureCheckerStore.getCurrentChecker()(id) : null;
  });

  readonly measureSimpleCheckers = computed<MeasureChecker[]>(() =>
    this.measureCheckers().filter(c => c.type === CheckerType.TWO_FIXED_LIMITS),
  );

  readonly measureFormulaCheckers = computed<MeasureChecker[]>(() =>
    this.measureCheckers().filter(c => c.type === CheckerType.TWO_POLYNOMIALS),
  );

  readonly measureLocations = computed<MeasureLocation[]>(() => {
    const id = this._id();
    return id ? this.measureLocationStore.entities().filter((ml: MeasureLocation) => ml.measureId === id) : [];
  });

  readonly locations = computed<Location[]>(() => {
    const locationIds = new Set(this.measureLocations().map(ml => ml.locationId));
    return Array.from(this._locationsMap())
      .filter(([id]) => locationIds.has(id))
      .map(([, location]) => location);
  });

  readonly units = computed<Unit[]>(() => this.unitStore.entities());

  private readonly crossStoreReferenceableMeasures = computed<ReferenceableMeasure[]>(() => {
    const combined: Measure[] = [
      ...this.manualMeasureStore.entities(),
      ...this.automaticMeasureStore.entities(),
      ...this.analysisPostStore.entities(),
    ];
    const cycleSafeMap = this._cycleSafeByMeasureId();
    if (cycleSafeMap.size === 0) return combined;
    return combined.map(m => {
      const cycleSafe = cycleSafeMap.get(m.id);
      return cycleSafe === undefined ? m : { ...m, cycleSafe };
    });
  });

  readonly measures = computed<ReferenceableMeasure[]>(() =>
    this._kind() === 'analysis-post' ? this.crossStoreReferenceableMeasures() : this.activeStore().entities(),
  );

  readonly measureTypes = computed<MeasureType[]>(() => this.measureTypeStore.entities());

  constructor() {
    effect(() => {
      if (this.measureLocations().length > 0) {
        void this.ensureLocationsLoaded();
      }
    });

    this.translateService.langChanges$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const id = this._id();
      if (id) {
        this._dataLoaded.set(false);
        void this.loadData(id);
      }
    });
  }

  setKind(kind: MeasureKind): void {
    if (this._kind() !== kind) {
      this._kind.set(kind);
    }
  }

  initRouteListener(): void {
    if (this.routeListenerActive) return;
    this.routeListenerActive = true;
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
        map(() => {
          const kind = this._kind();
          return kind ? RouteUtils.findMeasureIdRoute(this.activatedRoute, kind) : null;
        }),
        filter((route): route is ActivatedRoute => !!route && route.snapshot.paramMap.has('id')),
        map(route => Number(route.snapshot.paramMap.get('id'))),
        filter(id => !isNaN(id)),
      )
      .subscribe(id => {
        void this.loadData(id);
      });
  }

  extractMeasureIdFromCurrentRoute(): number | null {
    const kind = this._kind();
    return kind ? RouteUtils.extractMeasureIdFromRoute(this.router, kind) : null;
  }

  async loadData(id: number): Promise<void> {
    if (this._id() === id && this._dataLoaded()) return;

    this._dataLoaded.set(false);
    this._id.set(id);

    try {
      await this.loadInitialData(id);
      const measure = this.activeStore().byId()(id);

      if (measure) {
        await Promise.all([this.loadLocationData(id), this.assetStore.loadById(measure.assetId)]);
        this.loadAssetMeasures(measure.assetId);
      }

      await this.ensureLocationsLoaded();
      this._dataLoaded.set(true);
    } catch (error) {
      const cause = error instanceof Error ? error.cause : error;
      if (cause instanceof HttpErrorResponse && cause.status === 404) {
        void this.router.navigate(['/404']);
      }
    }
  }

  private loadInitialData(id: number): Promise<unknown> {
    return Promise.all([
      this.activeStore().loadById(id),
      this.measureTypeStore.loadAll(),
      this.unitStore.loadAll(),
      this.measureFormulaStore.getByMeasureId(id),
      this.measureCheckerStore.getByMeasureId(id),
      this.measureLocationStore.getByMeasureId(id),
      this.loadReferencingAnalysisPosts(id),
      ...this.kindSpecificInitTasks(id),
    ]);
  }

  private async loadReferencingAnalysisPosts(id: number): Promise<void> {
    this._referencingAnalysisPosts.set([]);
    const kind = this._kind();
    const request =
      kind === 'automatic'
        ? this.automaticMeasureApi.getAutomaticMeasureReferencingAnalysisPosts({ id })
        : kind === 'analysis-post'
          ? this.analysisPostApi.getAnalysisPostReferencingAnalysisPosts({ id })
          : this.manualMeasureApi.getManualMeasureReferencingAnalysisPosts({ id });
    this._referencingAnalysisPosts.set(await firstValueFrom(request));
  }

  private kindSpecificInitTasks(id: number): Promise<unknown>[] {
    return this._kind() === 'analysis-post' ? [this.refreshCycleSafeCandidates(id)] : [];
  }

  private kindSpecificAssetMeasureTasks(assetId: number): Promise<unknown>[] {
    return this._kind() === 'analysis-post'
      ? [this.manualMeasureStore.getByAssetId(assetId), this.automaticMeasureStore.getByAssetId(assetId)]
      : [];
  }

  private async refreshCycleSafeCandidates(measureId: number): Promise<void> {
    try {
      const response = await firstValueFrom(this.analysisPostApi.getAnalysisPostCycleCandidates({ measureId }));
      this._cycleSafeByMeasureId.set(new Map<number, boolean>(response.items.map(c => [c.id, c.cycleSafe])));
    } catch {
      this._cycleSafeByMeasureId.set(new Map());
    }
  }

  private loadAssetMeasures(assetId: number): void {
    if (!assetId) return;
    Promise.all([
      this.activeStore().getByAssetId(assetId),
      this.auscultationInfoStore.getByAssetId(assetId),
      ...this.kindSpecificAssetMeasureTasks(assetId),
    ]).catch(error => {
      if (isRequestCancellation(error)) return;
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    });
  }

  private async loadLocationData(id: number): Promise<void> {
    try {
      const measure = this.activeStore().byId()(id);
      if (measure) {
        await this.locationStore.getLocationsByAsset(measure.assetId);
      }
    } catch (error) {
      if (isRequestCancellation(error)) return;
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    }
  }

  async ensureLocationsLoaded(): Promise<void> {
    const measureLocations = this.measureLocations();
    if (measureLocations.length === 0) return;

    const measure = this.measure();
    if (!measure) return;

    await this.locationStore.getLocationsByAsset(measure.assetId);

    const currentMap = this._locationsMap();
    const missingLocationIds = measureLocations.map(ml => ml.locationId).filter(id => !currentMap.has(id));

    if (missingLocationIds.length === 0) return;

    try {
      const loaded = await Promise.all(missingLocationIds.map(id => this.locationStore.loadById(id)));
      const next = new Map(currentMap);
      loaded.forEach(location => location && next.set(location.id, location));
      this._locationsMap.set(next);
    } catch (error) {
      if (isRequestCancellation(error)) return;
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    }
  }

  async createChecker(checker: Omit<MeasureChecker, 'id'>) {
    const id = this._id();
    if (!id) throw new Error('No measure ID available');
    return this.measureCheckerStore.createForMeasure(id, checker);
  }

  async updateChecker(checkerId: number, changes: Partial<MeasureChecker>) {
    return this.measureCheckerStore.update(checkerId, changes);
  }

  async deleteChecker(checkerId: number) {
    return this.measureCheckerStore.delete(checkerId);
  }

  deleteMeasureFormula = this.measureFormulaStore.delete;
  createMeasureFormulaForMeasure = this.measureFormulaStore.createForMeasure;
  getMeasureFormulaByMeasureId = this.measureFormulaStore.getByMeasureId;
  updateMeasureFormula = this.measureFormulaStore.update;
  suspendMeasure = this.measureFormulaStore.suspendMeasure;
  reactivateMeasure = this.measureFormulaStore.reactivateMeasure;
  invalidateMeasureFormulaCache = this.measureFormulaStore.invalidateMeasureCache;

  /** Ce que la suppression du poste emporte — alimente les lignes de la confirmation. */
  async loadDeletionImpact(id: number): Promise<AnalysisPostDeletionImpact> {
    return firstValueFrom(this.analysisPostApi.getAnalysisPostDeletionImpact({ id }));
  }

  async deleteMeasure(): Promise<void> {
    const id = this._id();
    if (!id) throw new Error('No measure ID available');
    await this.activeStore().delete(id);
    if (this._kind() === 'analysis-post') this._cycleSafeByMeasureId.set(new Map());
  }

  async updateMeasure(changes: Partial<Measure>): Promise<void> {
    const id = this._id();
    if (!id) throw new Error('No measure ID available');
    await this.activeStore().update(id, changes);
  }

  private sortByDateDesc<T extends TimeStampedEntity & { id: number }>(items: T[]): T[] {
    return [...items].sort((a, b) => {
      if (!a.startTs && !b.startTs) return a.id - b.id;
      if (!a.startTs) return 1;
      if (!b.startTs) return -1;
      const dateA = new Date(a.startTs).getTime();
      const dateB = new Date(b.startTs).getTime();
      if (isNaN(dateA) && isNaN(dateB)) return a.id - b.id;
      if (isNaN(dateA)) return 1;
      if (isNaN(dateB)) return -1;
      if (dateA === dateB) return a.id - b.id;
      return dateB - dateA;
    });
  }

  stopDataLoading(): void {
    this._dataLoaded.set(false);
  }
}
